import { DEFAULT_BRAND, useBrandKit } from '../brand/BrandKitContext.tsx'
import { HEX_COLOUR } from '../brand/palette.ts'
import { ThemeToggle } from './ThemeToggle.tsx'

// Placeholders are Astute’s original wording, never pre-filled into `value`.
// A blank field visibly means "keep the default", and keeps the client from
// forking the defaults that live server-side in server/merge-defaults.ts.
const ROLE_FIELDS = [
  { key: 'role_approved_provider', label: 'Approved Provider', placeholder: 'Approved Provider' },
  { key: 'role_nominated_supervisor', label: 'Nominated Supervisor', placeholder: 'Nominated Supervisor' },
  // The apostrophe is U+2019 (typographic), not U+0027. Verify by bytes, not by eye.
  { key: 'role_consultant', label: 'Consultant', placeholder: 'Children’s Services Consultant' },
  { key: 'role_policy_owner', label: 'Policy owner', placeholder: 'GM Policy & Governance' },
]
const PLATFORM_FIELDS = [
  { key: 'platform_compliance_system', label: 'Compliance system', placeholder: '1Place' },
  { key: 'platform_child_video', label: 'Child-safe video platform', placeholder: 'Kids YouTube' },
  { key: 'platform_antivirus', label: 'Antivirus', placeholder: 'Norton, McAfee' },
]

const hasOverride = (overrides: Record<string, string>) =>
  Object.values(overrides).some((v) => typeof v === 'string' && v.trim())

/**
 * The /settings route. Every control here is wired to state that already
 * exists (the theme mode and the brand kit) and persists itself on change, so
 * there is no Save button and nothing on this page is a placeholder.
 */
export function SettingsPage() {
  const { brand, setBrand } = useBrandKit()
  const isDefault =
    brand.name === DEFAULT_BRAND.name &&
    brand.primary === DEFAULT_BRAND.primary &&
    !hasOverride(brand.roles) &&
    !hasOverride(brand.platforms)

  return (
    <main className="settings">
      <p className="eyebrow">Admin</p>
      <h1 className="tool__title">Settings</h1>
      <p className="tool__lead">
        Make the office your own: how it looks, what your service is called, and the words your
        documents use. Everything saves itself as you go, and stays in this browser.
      </p>

      <section className="panel settings__panel">
        <div className="settings__intro">
          <h2 className="settings__head">Appearance</h2>
          <p className="settings__hint">
            Light, dark, or let the office follow whatever your computer is set to. The same three
            buttons sit in the top bar if you would rather switch while you work.
          </p>
        </div>
        {/* Pinned open: here the control is the subject, not a compact affordance. */}
        <ThemeToggle expanded />
      </section>

      <section className="panel settings__panel">
        <div className="settings__intro">
          <h2 className="settings__head">Brand</h2>
          <p className="settings__hint">
            The name you set here appears across the app and on every document you download, so it
            is what families and regulators see on your paperwork.
          </p>
        </div>

        <div className="field settings__field">
          <label className="field__label" htmlFor="brand-name">
            Organisation name
          </label>
          <input
            id="brand-name"
            className="input"
            value={brand.name}
            onChange={(e) => setBrand({ ...brand, name: e.target.value })}
          />
        </div>

        <div className="field settings__field">
          <label className="field__label" htmlFor="brand-primary">
            Primary colour
          </label>
          <div className="settings__colour">
            <input
              id="brand-primary"
              type="color"
              className="swatch"
              value={HEX_COLOUR.test(brand.primary) ? brand.primary : DEFAULT_BRAND.primary}
              onChange={(e) => setBrand({ ...brand, primary: e.target.value })}
            />
            <input
              className="input"
              aria-label="Primary colour hex value"
              value={brand.primary}
              // A half-typed hex is stored but never applied: setProperty ignores
              // an invalid value, so the accent holds until the value is complete.
              onChange={(e) => {
                const typed = e.target.value.trim()
                setBrand({ ...brand, primary: typed && !typed.startsWith('#') ? `#${typed}` : typed })
              }}
            />
          </div>
          <p className="settings__note">
            Your colour becomes the accent, and the pages behind it pick up a matching tint so the
            two sit well together. Light and dark are worked out separately, so it reads clearly
            in both.
          </p>
        </div>

        <button
          type="button"
          className="btn btn--ghost"
          disabled={isDefault}
          onClick={() => setBrand({ ...DEFAULT_BRAND })}
        >
          Reset to defaults
        </button>
        <p className="settings__note">
          Reset puts the name, the colour and any wording below back to Astute’s. Your light and
          dark choice is left alone.
        </p>
      </section>

      <section className="panel settings__panel">
        <div className="settings__intro">
          <h2 className="settings__head">Role titles</h2>
          <p className="settings__hint">
            Leave a field blank to keep Astute’s current wording, which is shown in grey inside the
            box. If your service uses a different title, type it here and your documents will use
            yours instead.
          </p>
        </div>

        {ROLE_FIELDS.map(({ key, label, placeholder }) => (
          <div className="field settings__field" key={key}>
            <label className="field__label" htmlFor={`brand-role-${key}`}>
              {label}
            </label>
            <input
              id={`brand-role-${key}`}
              className="input"
              placeholder={placeholder}
              value={brand.roles[key] ?? ''}
              onChange={(e) =>
                setBrand({ ...brand, roles: { ...brand.roles, [key]: e.target.value } })
              }
            />
          </div>
        ))}
      </section>

      <section className="panel settings__panel">
        <div className="settings__intro">
          <h2 className="settings__head">Platforms</h2>
          <p className="settings__hint">
            Same idea for the systems your service runs on. Name your own here and the steps in
            your documents will point staff at software they can actually open.
          </p>
        </div>

        {PLATFORM_FIELDS.map(({ key, label, placeholder }) => (
          <div className="field settings__field" key={key}>
            <label className="field__label" htmlFor={`brand-platform-${key}`}>
              {label}
            </label>
            <input
              id={`brand-platform-${key}`}
              className="input"
              placeholder={placeholder}
              value={brand.platforms[key] ?? ''}
              onChange={(e) =>
                setBrand({ ...brand, platforms: { ...brand.platforms, [key]: e.target.value } })
              }
            />
          </div>
        ))}
      </section>

      <section className="panel settings__panel">
        <div className="settings__intro">
          <h2 className="settings__head">Access</h2>
          <p className="settings__hint">
            Everyone signs in with the same shared password during this pilot, so there is nothing
            to manage here yet. Individual sign-in and roles arrive in Phase 2.
          </p>
        </div>
      </section>
    </main>
  )
}
